import { useState } from 'react';
import { projectFormatter } from '../common/projectFormatter.js';
import { ProgressBar } from '../common/ProgressBar.js';
import { GiveWidget } from '../common/GiveWidget.js';
import { TrackSectionView } from '../common/TrackSectionView.tsx';
import { Svg } from '../common/Svg.js';
import { Swipeable } from './Swipeable';
import PropTypes from 'prop-types';

export const MarketingTeacherCards = ({ teachers, data }) => {
    const [index, setIndex] = useState(0);
    const [showGive, setShowGive] = useState(false);
    const [swipeState, setSwipeState] = useState(null);

    const getProposalURL = () => {
        return projectFormatter.removeUTMParams(teachers[index].proposalURL, teachers[index].teacherId);
    };

    const onSwipeLeft = () => {
        setSwipeState('left');
        changeCard();
    };
    const onSwipeRight = () => {
        setSwipeState('right');
        changeCard();
    };
    const onSwipeAnimationEnd = (e) => {
        if (e.animationName === 'swipeLeft' || e.animationName === 'swipeRight') {
            setSwipeState(null);
        }
    };

    const changeCard = () => {
        setShowGive(false);

        if (teachers.length === index + 1) {
            setIndex(0);
        } else {
            setIndex(index + 1);
        }
    };

    let swipeClass = '';
    if (swipeState === 'left') {
        swipeClass = 'swipeLeft';
    } else if (swipeState === 'right') {
        swipeClass = 'swipeRight';
    }

    const progressBar = () => {
        return (
            <div className="progress-bar-container">
                <div className="project-stats">
                    <div className="donors">{teachers[index].numDonors} DONORS</div>
                    <div className="still-needed"><strong>${teachers[index].costToComplete.toFixed(0)}</strong> STILL NEEDED</div>
                </div>
                <ProgressBar project={teachers[index]}/>
            </div>
        );
    };

    const renderGiveBar = () => {
        if (showGive) {
            return (
                <div className="give-bar">
                    <GiveWidget project={teachers[index]} projectId={teachers[index].id} showGiveWidget={showGive} challengeId={data.challengeId} showPrimaryGiveButton={true} hideCompleteProjectCallout={true}/>
                    <button type="button" className="button-secondary" onClick={changeCard}>Next</button>
                </div>);
        } else {
            return (
                <div className="give-bar">
                    <button type="button" className="button-primary" onClick={() => setShowGive(data.active)}>Give</button>
                    <button type="button" className="button-secondary" onClick={changeCard}>Next</button>
                </div>
            );
        }
    };

    const renderTeacherLocation = (teacher) => {
        if (teacher.location == null || teacher.location === '' || teacher.hideDetails) {
            return null;
        }
        return (<div className="teacher-info-wrapper"><span className="location">{teacher.location}</span></div>);
    };

    return (
        <div className="stack-container">
            <Swipeable onSwipeLeft={onSwipeLeft} onSwipeRight={onSwipeRight}>
                <div className={`teacher-swipe-card ${swipeClass}`} onAnimationEnd={onSwipeAnimationEnd}>
                    <div className="content-container">
                        <TrackSectionView sectionName={'Teacher Cards'}>
                            <div className="needs-statement">
                                {teachers[index].fulfillmentTrailer}
                                <a className="read-more text-link" href={baseFrontURL + getProposalURL()}>Read
                                    about {teachers[index].teacherName}&#39;s request</a>
                            </div>
                            <div className="teacher-opening">
                                <div className="teacher-info-wrapper">
                                    {teachers[index].matchMultiplier && (
                                        <div className="match-indicator">Matched {teachers[index].matchMultiplier.toLowerCase()}!</div>
                                    )}
                                    <div className="teacher-name">{teachers[index].teacherName}</div>
                                    {!teachers[index].hideDetails &&
                                        <div className="school-name">{teachers[index].schoolName}</div>}
                                </div>
                                <img className="teacher-photo" src={teachers[index].profilePhotoURL} alt="" key={teachers[index].profilePhotoURL}/>
                            </div>
                            <div className="teacher-info-wrapper secondary">
                                {renderTeacherLocation(teachers[index])}
                                <div className="economic-descriptor">
                                    {teachers[index].povertyLevel}
                                    <span className="tooltip-wrapper" tabIndex="0">
                                        <Svg icon="help"/>
                                        <span className="hover-tooltip">Data about students&#39; economic need comes from the National Center for Education Statistics, via our partners at MDR Education. <a
                                            href="https://help.donorschoose.org/hc/en-us/articles/202375748-How-is-a-school-s-economic-need-level-defined-at-DonorsChoose-">Learn more</a>
                                        </span>
                                    </span>
                                </div>
                                {teachers[index].hasBeenFunded && <span className="nbf">Never before funded</span>}
                                <span className="students-reached"><strong>{teachers[index].numStudents}</strong> students impacted</span>
                            </div>
                        </TrackSectionView>
                        {!isMobileDevice &&
                            <div className="swipe-controls">
                                {progressBar()}
                                {renderGiveBar()}
                            </div>
                        }
                    </div>
                </div>
            </Swipeable>
            <div className="background-card"></div>
            {isMobileDevice &&
                <div className="swipe-controls">
                    {progressBar()}
                    {renderGiveBar()}
                </div>
            }
        </div>
    );
};

MarketingTeacherCards.propTypes = {
    teachers: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.number.isRequired,
        title: PropTypes.string.isRequired,
        fulfillmentTrailer: PropTypes.string.isRequired,
        hasReceivedDonations: PropTypes.bool.isRequired,
        teacherName: PropTypes.string.isRequired,
        schoolName: PropTypes.string.isRequired,
        costToComplete: PropTypes.number.isRequired,
        totalPrice: PropTypes.number.isRequired,
        proposalURL: PropTypes.string.isRequired,
        profilePhotoURL: PropTypes.string.isRequired,
        essay: PropTypes.string.isRequired,
        povertyLevel: PropTypes.string.isRequired,
        hasBeenFunded: PropTypes.bool.isRequired,
        numStudents: PropTypes.number.isRequired,
        numDonors: PropTypes.number.isRequired,
        location: PropTypes.string,
        hideDetails: PropTypes.bool.isRequired,
        teacherId: PropTypes.number,
        matchMultiplier: PropTypes.string,
    })),
    data: PropTypes.shape({
        challengeId: PropTypes.number.isRequired,
        active: PropTypes.bool.isRequired,
    }),
};
